import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CATEGORIES, ANSWER_OPTIONS } from "@/lib/data";
import { scoreByCategory } from "@/lib/scoring";
import { api, storeResults } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Quiz() {
  const nav = useNavigate();
  const { user, refreshLatest } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingQ, setLoadingQ] = useState(true);

  useEffect(() => {
    api.get("/questions").then((r) => {
      const qs = (r.data.questions || []).map((q) => ({
        id: q.id, category: q.category,
        categoryLabel: q.category_label || CATEGORIES.find(c => c.key === q.category)?.label || q.category,
        measure: q.measure, text: q.text,
      }));
      setQuestions(qs);
    }).finally(() => setLoadingQ(false));
  }, []);

  if (loadingQ) return <div className="text-center py-24 text-neutral-500">Sorular yükleniyor...</div>;
  if (questions.length === 0) return <div className="text-center py-24 text-neutral-500">Henüz soru tanımlı değil.</div>;

  const q = questions[idx];
  const progress = ((idx + 1) / questions.length) * 100;
  const current = answers[q.id];
  const activeCat = q.category;

  const pick = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));
  const back = () => idx > 0 && setIdx(idx - 1);

  const next = async () => {
    if (!current) return;
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    const results = scoreByCategory(answers, questions);
    const detailed = questions.map((qq) => {
      const opt = ANSWER_OPTIONS.find((o) => o.value === Number(answers[qq.id]));
      return {
        questionId: qq.id, category: qq.category, categoryLabel: qq.categoryLabel,
        measure: qq.measure, questionText: qq.text,
        answerValue: opt.value, answerLabel: opt.label,
      };
    });
    storeResults(results);
    try {
      await api.post("/submissions", {
        answers: detailed,
        scores: results.scores,
        general_score: results.general,
        strongest_areas: results.strongest,
        development_areas: results.development,
        recommended_resources: results.recommended_resources,
        recommended_books: results.recommended_books,
      });
      await refreshLatest();
    } catch (e) {
      console.error("Submission save failed", e);
      toast.error("Sonuç kaydedilemedi ama skorunu görebilirsin.");
    }
    setSubmitting(false);
    nav("/skor");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="quiz-page">
      <aside className="lg:col-span-3 order-2 lg:order-1">
        <div className="glass p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">10 Temel Yaşam Alanı</div>
          <ul className="space-y-2.5">
            {CATEGORIES.map((c) => {
              const Icon = Icons[c.icon] || Icons.Circle;
              const active = c.key === activeCat;
              return (
                <li key={c.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${active ? "bg-white/5 border border-white/15" : "border border-transparent"}`}>
                  <Icon size={16} className={active ? "text-white" : "text-neutral-500"} />
                  <span className={`text-sm ${active ? "text-white" : "text-neutral-400"}`}>{c.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      <section className="lg:col-span-6 order-1 lg:order-2">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight h-gradient text-center">Life 101 Testi</h1>
        <p className="mt-3 text-center text-neutral-400">{user?.first_name && `${user.first_name}, `}{questions.length} soruluk analiz ile 10 temel yaşam alanında mevcut durumunu ölç.</p>

        <div className="glass mt-8 p-6 md:p-8">
          <div className="flex items-center justify-between text-sm text-neutral-400">
            <span>%{Math.round(progress)}</span>
            <span data-testid="quiz-counter">{idx + 1} / {questions.length} soru</span>
          </div>
          <div className="score-bar mt-2"><div style={{ width: `${progress}%` }} /></div>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{idx + 1}. {q.categoryLabel} — {q.measure}</div>
            <h2 className="mt-3 text-2xl md:text-3xl font-medium leading-snug text-white" data-testid="quiz-question">{q.text}</h2>
            <div className="mt-7 space-y-3">
              {ANSWER_OPTIONS.map((o) => (
                <button type="button" key={o.value} className={`option-row w-full text-left ${current === o.value ? "selected" : ""}`} onClick={() => pick(o.value)} data-testid={`quiz-option-${o.value}`}>
                  <span className="radio-dot" />
                  <span className="text-neutral-200">{o.label}</span>
                </button>
              ))}
            </div>
            <div className="mt-9 flex items-center justify-between">
              <button onClick={back} disabled={idx === 0} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-40" data-testid="quiz-back">
                <ArrowLeft size={16} /> Geri
              </button>
              <button onClick={next} disabled={!current || submitting} className="btn-primary inline-flex items-center gap-2" data-testid="quiz-next">
                {idx === questions.length - 1 ? (submitting ? "Hesaplanıyor..." : "Sonucu Gör") : "Devam Et"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <aside className="lg:col-span-3 order-3">
        <div className="glass p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Test Hakkında</div>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li>• {questions.length} soru</li>
            <li>• Yaklaşık 7-10 dakika</li>
            <li>• 10 alan analizi</li>
            <li>• Kişisel skor</li>
            <li>• AI destekli rapor</li>
            <li>• Ücretsiz kaynak önerileri</li>
            <li>• Kitap önerileri</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
